// Default gym workouts: sets and reps with barbell, dumbbells, cables and machines, plus cardio-machine intervals.
// Our own plans; "inspired by" names the idea, no text is copied.
import type { Workout } from '../types';
import { rampUp } from './common';
import { ids, reps, t } from './plan';

const FIVE_BY_FIVE = { label: 'Inspired by classic 5x5 barbell programs such as StrongLifts 5x5; our own summary' };

export const GYM_WORKOUTS: Workout[] = [
  {
    id: 'gym_push_day',
    name: 'Push Day',
    description: 'Chest, shoulders and triceps: a heavy bench press, then dumbbell presses and lighter isolation work.',
    level: 'intermediate',
    focus: 'upper_body',
    equipment: ['barbell', 'bench', 'dumbbell', 'cable', 'machine'],
    xp: 90,
    source: { label: 'Our own, push/pull/legs split' },
    warmupId: 'gym',
    cooldownId: 'upper_back',
    sections: [
      rampUp('bench_press'),
      { id: 'heavy', kind: 'main', format: { kind: 'sets', sets: 4, restSec: 150 }, blocks: ids('heavy', [
        { exerciseId: 'bench_press', amount: reps(6, { max: 8 }) },
      ]) },
      { id: 'press', kind: 'main', format: { kind: 'sets', sets: 3, restSec: 90 }, blocks: ids('press', [
        { exerciseId: 'incline_dumbbell_press', amount: reps(8, { max: 10 }) },
        { exerciseId: 'dumbbell_shoulder_press', amount: reps(8, { max: 10 }) },
      ]) },
      { id: 'accessory', kind: 'main', format: { kind: 'sets', sets: 3, restSec: 60 }, blocks: ids('accessory', [
        { exerciseId: 'lateral_raise', amount: reps(12, { max: 15 }) },
        { exerciseId: 'cable_fly', amount: reps(12) },
        { exerciseId: 'triceps_pushdown', amount: reps(10, { max: 12 }) },
      ]) },
    ],
  },
  {
    id: 'gym_pull_day',
    name: 'Pull Day',
    description: 'Back and biceps: deadlifts first, then pulldowns, rows, face pulls and curls.',
    level: 'intermediate',
    focus: 'upper_body',
    equipment: ['barbell', 'cable', 'dumbbell', 'machine'],
    xp: 90,
    source: { label: 'Our own, push/pull/legs split' },
    warmupId: 'gym',
    cooldownId: 'upper_back',
    sections: [
      rampUp('deadlift'),
      { id: 'heavy', kind: 'main', format: { kind: 'sets', sets: 3, restSec: 180 }, blocks: ids('heavy', [
        { exerciseId: 'deadlift', amount: reps(5) },
      ]) },
      { id: 'pull', kind: 'main', format: { kind: 'sets', sets: 3, restSec: 90 }, blocks: ids('pull', [
        { exerciseId: 'lat_pulldown', amount: reps(8, { max: 12 }) },
        { exerciseId: 'barbell_row', amount: reps(8) },
        { exerciseId: 'face_pull', amount: reps(15) },
      ]) },
      { id: 'arms', kind: 'main', format: { kind: 'sets', sets: 3, restSec: 60 }, blocks: ids('arms', [
        { exerciseId: 'dumbbell_curl', amount: reps(10, { max: 12 }) },
        { exerciseId: 'hammer_curl', amount: reps(12) },
      ]) },
    ],
  },
  {
    id: 'gym_leg_day',
    name: 'Leg Day',
    description: 'Squats, Romanian deadlifts and the leg press, then hamstrings, calves and abs.',
    level: 'intermediate',
    focus: 'lower_body',
    equipment: ['barbell', 'machine', 'cable'],
    xp: 95,
    source: { label: 'Our own, push/pull/legs split' },
    warmupId: 'gym',
    cooldownId: 'legs_hips',
    sections: [
      rampUp('back_squat'),
      { id: 'heavy', kind: 'main', format: { kind: 'sets', sets: 4, restSec: 150 }, blocks: ids('heavy', [
        { exerciseId: 'back_squat', amount: reps(6, { max: 8 }) },
      ]) },
      { id: 'strength', kind: 'main', format: { kind: 'sets', sets: 3, restSec: 120 }, blocks: ids('strength', [
        { exerciseId: 'romanian_deadlift', amount: reps(8, { max: 10 }) },
        { exerciseId: 'leg_press', amount: reps(10, { max: 12 }) },
      ]) },
      { id: 'accessory', kind: 'main', format: { kind: 'sets', sets: 3, restSec: 60 }, blocks: ids('accessory', [
        { exerciseId: 'lying_leg_curl', amount: reps(12) },
        { exerciseId: 'standing_calf_raise_machine', amount: reps(15) },
        { exerciseId: 'cable_crunch', amount: reps(12) },
      ]) },
    ],
  },
  {
    id: 'full_body_gym_45',
    name: 'Full Body Gym 45',
    description: 'One session for the whole body: hinge, press, pull, lunge and a plank, three sets each.',
    level: 'beginner',
    focus: 'full_body',
    equipment: ['barbell', 'dumbbell', 'bench', 'cable', 'machine'],
    xp: 85,
    source: { label: 'Our own' },
    warmupId: 'gym',
    cooldownId: 'full_body',
    sections: [
      rampUp('trap_bar_deadlift'),
      { id: 'main', kind: 'main', format: { kind: 'sets', sets: 3, restSec: 90 }, blocks: ids('main', [
        { exerciseId: 'trap_bar_deadlift', amount: reps(6) },
        { exerciseId: 'dumbbell_bench_press', amount: reps(10) },
        { exerciseId: 'seated_cable_row', amount: reps(10) },
        { exerciseId: 'dumbbell_lunge', amount: reps(8, { perSide: true }) },
        { exerciseId: 'dumbbell_shoulder_press', amount: reps(10) },
        { exerciseId: 'plank', amount: t(45) },
      ]) },
    ],
  },
  {
    id: 'strength_5x5_a',
    name: '5x5 Strength A',
    description: 'Squat, bench press and barbell row, five sets of five. Alternate with B; add a little weight each time all sets go well.',
    level: 'beginner',
    focus: 'strength',
    equipment: ['barbell', 'bench', 'machine'],
    xp: 80,
    source: FIVE_BY_FIVE,
    warmupId: 'gym',
    cooldownId: 'full_body',
    sections: [
      rampUp('back_squat'),
      { id: 'main', kind: 'main', format: { kind: 'sets', sets: 5, restSec: 180 }, blocks: ids('main', [
        { exerciseId: 'back_squat', amount: reps(5) },
        { exerciseId: 'bench_press', amount: reps(5) },
        { exerciseId: 'barbell_row', amount: reps(5) },
      ]) },
    ],
  },
  {
    id: 'strength_5x5_b',
    name: '5x5 Strength B',
    description: 'Squat and overhead press five sets of five, then one heavy set of five deadlifts. Alternate with A.',
    level: 'beginner',
    focus: 'strength',
    equipment: ['barbell', 'machine'],
    xp: 70,
    source: FIVE_BY_FIVE,
    warmupId: 'gym',
    cooldownId: 'full_body',
    sections: [
      rampUp('back_squat'),
      { id: 'main', kind: 'main', format: { kind: 'sets', sets: 5, restSec: 180 }, blocks: ids('main', [
        { exerciseId: 'back_squat', amount: reps(5) },
        { exerciseId: 'overhead_press', amount: reps(5) },
      ]) },
      // Working up to one heavy set takes several lighter sets with time to load plates; plan them, so the length is honest.
      { id: 'deadlift_ramp', kind: 'main', format: { kind: 'sets', sets: 4, restSec: 120 }, blocks: ids('deadlift_ramp', [
        { exerciseId: 'deadlift', amount: reps(5), note: 'ramp-up sets, light to heavy' },
      ]) },
      { id: 'deadlift', kind: 'main', format: { kind: 'sets', sets: 1, restSec: 0 }, blocks: ids('deadlift', [
        { exerciseId: 'deadlift', amount: reps(5), note: 'one heavy set' },
      ]) },
    ],
  },
  {
    id: 'dumbbell_full_body_30',
    name: 'Dumbbell Full Body 30',
    description: 'A pair of dumbbells at home or in the gym: squat, hinge, press, row and lunge in three sets.',
    level: 'beginner',
    focus: 'full_body',
    equipment: ['dumbbell'],
    xp: 60,
    source: { label: 'Our own' },
    warmupId: 'general',
    cooldownId: 'full_body',
    sections: [
      { id: 'main', kind: 'main', format: { kind: 'sets', sets: 3, restSec: 60 }, blocks: ids('main', [
        { exerciseId: 'dumbbell_goblet_squat', amount: reps(12) },
        { exerciseId: 'dumbbell_rdl', amount: reps(10) },
        { exerciseId: 'dumbbell_floor_press', amount: reps(10) },
        { exerciseId: 'dumbbell_bent_over_row', amount: reps(10) },
        { exerciseId: 'dumbbell_lunge', amount: reps(8, { perSide: true }) },
      ]) },
    ],
  },
  {
    id: 'rower_intervals_20',
    name: 'Rower Intervals 20',
    description: 'Eight hard minutes on the rower, each followed by an easy minute, between an easy start and finish.',
    level: 'intermediate',
    focus: 'conditioning',
    equipment: ['machine'],
    xp: 55,
    source: { label: 'Our own' },
    warmupId: 'gym',
    cooldownId: 'full_body',
    sections: [
      { id: 'main', kind: 'main', format: { kind: 'circuit', rounds: 8, workSec: 60, restSec: 0 }, blocks: ids('main', [
        { exerciseId: 'rowing_machine', note: 'hard, about 8 of 10' },
        { exerciseId: 'rowing_machine', note: 'easy, keep moving' },
      ]) },
    ],
  },
  {
    id: 'jump_rope_hiit_15',
    name: 'Jump Rope HIIT 15',
    description: 'Rope intervals with squats and push-ups in between: 30 seconds on, 15 off, four rounds.',
    level: 'intermediate',
    focus: 'conditioning',
    equipment: ['jump_rope'],
    xp: 40,
    source: { label: 'Our own' },
    warmupId: 'running_drills',
    cooldownId: 'runners',
    sections: [
      { id: 'main', kind: 'main', format: { kind: 'circuit', rounds: 4, workSec: 30, restSec: 15 }, blocks: ids('main', [
        { exerciseId: 'jump_rope_basic' },
        { exerciseId: 'squat' },
        { exerciseId: 'jump_rope_alternate_foot' },
        { exerciseId: 'push_up' },
      ]) },
    ],
  },
];
